import { useState, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { PageLoader, EmptyState } from "@/components/PageStates"
import { Palette, Check, Plus, Trash2 } from "lucide-react"
import { cn } from "@/lib/utils"

function ThemeCard({ theme, isActive, onActivate }) {
  return (
    <div
      className={cn(
        "relative rounded-xl border-2 transition-all duration-200 cursor-pointer overflow-hidden",
        isActive
          ? "border-[hsl(var(--brand-500))] shadow-md"
          : "border-[hsl(var(--border-default))] hover:border-[hsl(var(--border-strong))] hover:shadow-sm"
      )}
      onClick={() => onActivate(theme.name)}
    >
      <div className="h-20 flex items-center justify-center" style={{ background: theme.background_color }}>
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm"
          style={{ background: theme.primary_color }}
        >
          <span className="text-white text-sm font-bold">K</span>
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-[hsl(var(--text-primary))]">{theme.display_name}</span>
          {isActive && (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--brand-500))]">
              <Check className="h-3 w-3 text-white" />
            </div>
          )}
        </div>
        <div className="flex gap-1 mt-2">
          <div className="w-4 h-4 rounded-full border" style={{ background: theme.primary_color }} />
          <div className="w-4 h-4 rounded-full border" style={{ background: theme.secondary_color }} />
          <div className="w-4 h-4 rounded-full border" style={{ background: theme.background_color }} />
          <div className="w-4 h-4 rounded-full border" style={{ background: theme.text_color }} />
        </div>
      </div>
    </div>
  )
}

export default function ThemesPage() {
  const queryClient = useQueryClient()

  const { data: themesData, isLoading } = useQuery({
    queryKey: ["themes"],
    queryFn: () => api.get("/themes"),
  })

  const activateMutation = useMutation({
    mutationFn: (name) =>
      fetch(`/api/themes/${name}/activate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }).then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries(["themes"])
    },
  })

  const themes = themesData?.data || []
  const activeTheme = themes.find((t) => t.is_active)

  if (isLoading) return <PageLoader />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[hsl(var(--text-primary))]">Themes</h1>
          <p className="text-sm text-[hsl(var(--text-secondary))]">Choose a theme for your dashboard</p>
        </div>
      </div>

      {themes.length === 0 ? (
        <EmptyState title="No themes" description="No themes available" />
      ) : (
        <>
          <div>
            <h2 className="text-lg font-semibold text-[hsl(var(--text-primary))] mb-3">Theme Presets</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {themes.map((theme) => (
                <ThemeCard
                  key={theme.name}
                  theme={theme}
                  isActive={theme.is_active}
                  onActivate={(name) => activateMutation.mutate(name)}
                />
              ))}
            </div>
          </div>

          {activeTheme && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Active Theme</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="flex gap-2">
                    <div className="w-8 h-8 rounded-full border" style={{ background: activeTheme.primary_color }} />
                    <div className="w-8 h-8 rounded-full border" style={{ background: activeTheme.secondary_color }} />
                    <div className="w-8 h-8 rounded-full border" style={{ background: activeTheme.background_color }} />
                    <div className="w-8 h-8 rounded-full border" style={{ background: activeTheme.text_color }} />
                  </div>
                  <div>
                    <p className="font-medium text-[hsl(var(--text-primary))]">{activeTheme.display_name}</p>
                    <p className="text-xs text-[hsl(var(--text-muted))]">
                      {activeTheme.is_dark ? "Dark" : "Light"} theme
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
